import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { ArrowLeft, Download, Plus, Trash2, Pencil } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { CatEventSelect } from '@/components/CatEventSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ClaimDetailPage = () => {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [claim, setClaim] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [feeChecks, setFeeChecks] = useState([]);
  const [users, setUsers] = useState([]);
  const [catEvents, setCatEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [deleteCheckId, setDeleteCheckId] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [feeCheckForm, setFeeCheckForm] = useState({
    check_number: '',
    check_amount: '',
    date_received: '',
    notes: ''
  });
  const [editForm, setEditForm] = useState({
    customer_name: '',
    date_of_loss: '',
    date_of_assignment: '',
    policy_number: '',
    claim_number: '',
    our_estimate_value: '',
    expected_loss_settlement: '',
    estimate_fee: '',
    fee_percentage: '',
    actual_loss_settlement: '',
    offer_received: false,
    status: 'Open',
    firm: '',
    internal_comments: '',
    notes: '',
    cat_event_id: ''
  });

  const fetchClaimDetails = useCallback(async () => {
    try {
      setLoading(true);
      
      const [claimRes, feeChecksRes, usersRes, catEventsRes] = await Promise.all([
        axios.get(`${API}/claims/${claimId}`),
        axios.get(`${API}/claims/${claimId}/fee-checks`),
        axios.get(`${API}/users/list`),
        axios.get(`${API}/cat-events`)
      ]);
      setCatEvents(catEventsRes.data);

      const claimData = claimRes.data;
      setClaim(claimData);
      setFeeChecks(feeChecksRes.data);
      setUsers(usersRes.data);

      // Fetch customer data only if customer_id exists
      if (claimData.customer_id) {
        try {
          const customerRes = await axios.get(`${API}/customers/${claimData.customer_id}`);
          setCustomer(customerRes.data);
        } catch (err) {
          setCustomer(null);
        }
      } else {
        setCustomer(null);
      }
      
      // Populate edit form with current claim data
      setEditForm({
        customer_name: claimData.customer_name || '',
        date_of_loss: claimData.date_of_loss || '',
        date_of_assignment: claimData.date_of_assignment || '',
        policy_number: claimData.policy_number || '',
        claim_number: claimData.claim_number || '',
        our_estimate_value: claimData.our_estimate_value || '',
        expected_loss_settlement: claimData.expected_loss_settlement || '',
        estimate_fee: claimData.estimate_fee || '',
        fee_percentage: claimData.fee_percentage || '',
        actual_loss_settlement: claimData.actual_loss_settlement || '',
        offer_received: claimData.offer_received || false,
        status: claimData.status || 'Open',
        firm: claimData.firm || '',
        internal_comments: claimData.internal_comments || '',
        notes: claimData.notes || '',
        cat_event_id: claimData.cat_event_id || ''
      });
    } catch (error) {
      toast.error('Failed to load claim details');
    } finally {
      setLoading(false);
    }
  }, [claimId]);

  useEffect(() => {
    fetchClaimDetails();
  }, [fetchClaimDetails]);

  const handleAddFeeCheck = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...feeCheckForm,
        check_amount: parseFloat(feeCheckForm.check_amount)
      };
      
      await axios.post(`${API}/claims/${claimId}/fee-checks`, payload);
      
      toast.success('Fee check added successfully');
      setIsDialogOpen(false);
      setFeeCheckForm({ check_number: '', check_amount: '', date_received: '', notes: '' });
      fetchClaimDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add fee check');
    }
  };

  const handleEditClaim = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        customer_name: editForm.customer_name || '',
        date_of_loss: editForm.date_of_loss || '',
        date_of_assignment: editForm.date_of_assignment || '',
        policy_number: editForm.policy_number || '',
        claim_number: editForm.claim_number || '',
        our_estimate_value: parseFloat(editForm.our_estimate_value) || 0,
        expected_loss_settlement: parseFloat(editForm.expected_loss_settlement) || 0,
        estimate_fee: parseFloat(editForm.estimate_fee) || 0,
        fee_percentage: editForm.fee_percentage ? parseFloat(editForm.fee_percentage) : null,
        actual_loss_settlement: parseFloat(editForm.actual_loss_settlement) || 0,
        offer_received: editForm.offer_received,
        status: editForm.status,
        firm: editForm.firm || '',
        internal_comments: editForm.internal_comments || '',
        notes: editForm.notes || '',
        cat_event_id: editForm.cat_event_id || ''
      };
      
      await axios.put(`${API}/claims/${claimId}`, payload);
      
      toast.success('Claim updated successfully');
      setIsEditDialogOpen(false);
      fetchClaimDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update claim');
    }
  };

  // Auto-calculate expected loss settlement when our estimate value changes
  const handleEditEstimateValueChange = (value) => {
    const estimateValue = parseFloat(value) || 0;
    const expectedSettlement = estimateValue * 0.75;
    setEditForm(prev => ({
      ...prev,
      our_estimate_value: value,
      expected_loss_settlement: expectedSettlement.toFixed(2)
    }));
    
    // Recalculate fee if percentage exists and no actual settlement
    if (editForm.fee_percentage && !editForm.actual_loss_settlement) {
      const feePercentage = parseFloat(editForm.fee_percentage);
      const calculatedFee = expectedSettlement * (feePercentage / 100);
      setEditForm(prev => ({
        ...prev,
        estimate_fee: calculatedFee.toFixed(2)
      }));
    }
  };

  // Auto-calculate estimate fee when fee percentage changes
  const handleEditFeePercentageChange = (value) => {
    const feePercentage = parseFloat(value) || 0;
    const actualSettlement = parseFloat(editForm.actual_loss_settlement);
    const expectedSettlement = parseFloat(editForm.expected_loss_settlement);
    const baseAmount = actualSettlement || expectedSettlement || 0;
    
    const calculatedFee = baseAmount * (feePercentage / 100);
    
    setEditForm(prev => ({
      ...prev,
      fee_percentage: value,
      estimate_fee: calculatedFee.toFixed(2)
    }));
  };

  // Recalculate fee when actual loss settlement changes
  const handleEditActualSettlementChange = (value) => {
    setEditForm(prev => ({
      ...prev,
      actual_loss_settlement: value
    }));
    
    if (editForm.fee_percentage) {
      const feePercentage = parseFloat(editForm.fee_percentage);
      const actualSettlement = parseFloat(value) || 0;
      const calculatedFee = actualSettlement * (feePercentage / 100);
      
      setEditForm(prev => ({
        ...prev,
        estimate_fee: calculatedFee.toFixed(2)
      }));
    }
  };

  const handleDeleteFeeCheck = (checkId) => {
    setDeleteCheckId(checkId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteFeeCheck = async () => {
    if (!deleteCheckId) return;
    
    try {
      await axios.delete(`${API}/claims/${claimId}/fee-checks/${deleteCheckId}`);
      
      toast.success('Payment record deleted successfully');
      setIsDeleteDialogOpen(false);
      setDeleteCheckId(null);
      fetchClaimDetails(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete payment record');
    }
  };

  const handleExportInvoice = async () => {
    try {
      const response = await axios.get(`${API}/export/claim/${claimId}/invoice`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${claimId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Invoice exported successfully');
    } catch (error) {
      toast.error('Failed to export invoice');
    }
  };

  const handleExportLossNotice = async () => {
    try {
      const response = await axios.get(`${API}/export/claim/${claimId}/loss-notice`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `loss_notice_${claimId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Loss Notice exported successfully');
    } catch (error) {
      toast.error('Failed to export loss notice');
    }
  };

  const handleExportClaimSummary = async () => {
    try {
      const response = await axios.get(`${API}/export/claim/${claimId}/summary`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `claim_summary_${claimId}.xls`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Claim Summary exported successfully');
    } catch (error) {
      toast.error('Failed to export claim summary');
    }
  };

  const canAddFeeCheck = hasRole(['admin', 'manager', 'staff']);
  const assignedCatEvent = claim ? catEvents.find(e => e.cat_event_id === claim.cat_event_id) : null;

  const renderCatEventValue = () => {
    if (!assignedCatEvent) {
      return <p className="text-sm text-foreground-muted" data-testid="claim-cat-event-none">Not assigned</p>;
    }
    const label = `${assignedCatEvent.cat_code} — ${assignedCatEvent.event_type}`;
    if (hasRole(['admin'])) {
      return (
        <Link
          to={`/cat-events/${assignedCatEvent.cat_event_id}`}
          className="font-medium text-accent hover:underline"
          data-testid="claim-cat-event-link"
        >
          {label}
        </Link>
      );
    }
    return <p className="font-medium text-foreground" data-testid="claim-cat-event-text">{label}</p>;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-screen" data-testid="loading-spinner">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!claim) {
    return (
      <DashboardLayout>
        <div className="p-8">
          <p className="text-foreground-muted">Claim not found</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="claim-detail-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/claims')} data-testid="back-button">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Claims
            </Button>
            <div>
              <h1 className="text-4xl font-bold font-sans text-foreground tracking-tight">
                {claim.claim_id}
              </h1>
              <p className="text-foreground-muted mt-1">
                Customer: {customer?.name || claim.customer_name || 'No Customer'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {canAddFeeCheck && (
              <Button variant="outline" onClick={() => setIsEditDialogOpen(true)} data-testid="edit-claim-button">
                <Pencil className="w-4 h-4 mr-2" />
                Edit Claim
              </Button>
            )}
            <Button variant="outline" onClick={handleExportLossNotice} data-testid="export-loss-notice-button">
              <Download className="w-4 h-4 mr-2" />
              Loss Notice
            </Button>
            <Button variant="outline" onClick={handleExportClaimSummary} data-testid="export-claim-summary-button">
              <Download className="w-4 h-4 mr-2" />
              Claim Summary
            </Button>
            <Button variant="outline" onClick={handleExportInvoice} data-testid="export-invoice-button">
              <Download className="w-4 h-4 mr-2" />
              Invoice
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Customer Info */}
          <Card className="p-6 border border-border shadow-sm h-fit" data-testid="customer-info-card">
            <h3 className="text-xl font-semibold font-sans text-foreground mb-4">Customer Information</h3>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-foreground-muted">Name</p>
                <p className="font-medium text-foreground">{customer?.name || claim.customer_name || 'No Customer'}</p>
              </div>
              {customer && (
                <>
                  <div>
                    <p className="text-foreground-muted">Email</p>
                  <p className="font-medium text-foreground">{customer.email}</p>
                </div>
                <div>
                  <p className="text-foreground-muted">Phone</p>
                  <p className="font-medium text-foreground font-mono text-xs">{customer.phone}</p>
                </div>
                <div>
                  <p className="text-foreground-muted">Risk Address</p>
                  <p className="font-medium text-foreground">{customer.risk_address}</p>
                </div>
                <div>
                  <p className="text-foreground-muted">Carrier</p>
                  <p className="font-medium text-foreground">{customer.insurance_company}</p>
                </div>
                <div>
                  <p className="text-foreground-muted">Claim Number</p>
                  <p className="font-medium text-foreground font-mono text-xs">{claim.claim_number || 'N/A'}</p>
                </div>
                </>
              )}
              {!customer && !claim.customer_name && (
                <p className="text-foreground-muted text-sm">No customer linked to this claim</p>
              )}
            </div>
          </Card>

          {/* Center Column - Claim Details */}
          <Card className="p-6 border border-border shadow-sm" data-testid="claim-details-card">
            <h3 className="text-xl font-semibold font-sans text-foreground mb-4">Claim Details</h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-foreground-muted mb-1">Status</p>
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-accent/20 text-accent">
                  {claim.status}
                </span>
              </div>
              <div>
                <p className="text-sm text-foreground-muted mb-1">Catastrophe Event</p>
                {renderCatEventValue()}
              </div>
              <div>
                <p className="text-sm text-foreground-muted mb-1">Our Estimate Value</p>
                <p className="text-lg font-bold font-mono text-foreground">
                  ${claim.our_estimate_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <p className="text-sm text-foreground-muted mb-1">Expected Loss Settlement</p>
                <p className="text-lg font-bold font-mono text-foreground">
                  ${claim.expected_loss_settlement.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
              </div>
              {claim.fee_percentage && (
                <div>
                  <p className="text-sm text-foreground-muted mb-1">Fee Percentage</p>
                  <p className="text-lg font-bold font-mono text-foreground">{claim.fee_percentage}%</p>
                </div>
              )}
              <div className="pt-4 border-t border-border">
                <p className="text-sm text-foreground-muted mb-1">Notes</p>
                <p className="text-sm text-foreground whitespace-pre-wrap">{claim.notes || 'No notes'}</p>
              </div>
              <div>
                <p className="text-sm text-foreground-muted mb-1">Internal Comments</p>
                <p className="text-sm text-foreground whitespace-pre-wrap">
                  {claim.internal_comments || 'No internal comments'}
                </p>
              </div>
            </div>
          </Card>

          {/* Right Column - Financials */}
          <div className="space-y-6">
            <Card className="p-6 border border-border shadow-sm" data-testid="financial-summary-card">
              <h3 className="text-xl font-semibold font-sans text-foreground mb-4">Financial Summary</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-foreground-muted">Estimate Fee</span>
                  <span className="font-bold font-mono text-foreground">
                    ${claim.estimate_fee.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-foreground-muted">Amount Paid</span>
                  <span className="font-bold font-mono text-success-text">
                    ${claim.amount_of_fee_paid.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-foreground font-medium">Fee Balance</span>
                  <span className={`font-bold font-mono text-xl ${
                    claim.fee_balance > 0 ? 'text-destructive' : 'text-success-text'
                  }`}>
                    ${claim.fee_balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </Card>

            <Card className="p-6 border border-border shadow-sm" data-testid="fee-checks-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold font-sans text-foreground">Fee Checks</h3>
                {canAddFeeCheck && (
                  <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-accent hover:bg-accent-hover" data-testid="add-fee-check-button">
                        <Plus className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add Fee Check</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleAddFeeCheck} className="space-y-4" data-testid="fee-check-form">
                        <div className="space-y-2">
                          <Label htmlFor="check_number">Check Number <span className="text-red-500">*</span></Label>
                          <Input
                            id="check_number"
                            value={feeCheckForm.check_number}
                            onChange={(e) => setFeeCheckForm({...feeCheckForm, check_number: e.target.value})}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="check_amount">Amount ($) <span className="text-red-500">*</span></Label>
                          <Input
                            id="check_amount"
                            type="number"
                            step="0.01"
                            value={feeCheckForm.check_amount}
                            onChange={(e) => setFeeCheckForm({...feeCheckForm, check_amount: e.target.value})}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="date_received">Date Received <span className="text-red-500">*</span></Label>
                          <Input
                            id="date_received"
                            type="date"
                            value={feeCheckForm.date_received}
                            onChange={(e) => setFeeCheckForm({...feeCheckForm, date_received: e.target.value})}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="notes">Notes</Label>
                          <Textarea
                            id="notes"
                            value={feeCheckForm.notes}
                            onChange={(e) => setFeeCheckForm({...feeCheckForm, notes: e.target.value})}
                            rows={3}
                          />
                        </div>
                        <div className="flex justify-end gap-4">
                          <Button type="button" variant="secondary" onClick={() => setIsDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button type="submit" className="bg-primary hover:bg-primary-hover" data-testid="submit-fee-check">
                            Add Check
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
              <div className="space-y-3">
                {feeChecks.length > 0 ? (
                  feeChecks.map((check) => (
                    <div 
                      key={check.check_id} 
                      className="p-4 border border-border rounded-md bg-slate-50/50"
                      data-testid={`fee-check-${check.check_id}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-medium text-foreground font-mono text-sm">Check #{check.check_number}</p>
                          <p className="text-xs text-foreground-muted">{check.date_received}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <p className="font-bold font-mono text-foreground">
                            ${parseFloat(check.check_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </p>
                          {hasRole(['admin', 'manager', 'staff']) && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteFeeCheck(check.check_id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-auto"
                              data-testid={`delete-check-${check.check_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      {check.notes && (
                        <p className="text-xs text-foreground-muted mt-2">{check.notes}</p>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-center text-foreground-muted py-8 text-sm">No fee checks recorded</p>
                )}
              </div>
            </Card>
          </div>
        </div>

        {/* Edit Claim Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-sans">Edit Claim</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditClaim} className="space-y-6" data-testid="edit-claim-form">
              <div className="space-y-2">
                <Label htmlFor="edit_customer_name">Customer Name</Label>
                <Input
                  id="edit_customer_name"
                  value={editForm.customer_name}
                  onChange={(e) => setEditForm({...editForm, customer_name: e.target.value})}
                  placeholder="Enter customer name"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_date_of_loss">Date of Loss</Label>
                  <Input
                    id="edit_date_of_loss"
                    type="date"
                    value={editForm.date_of_loss}
                    onChange={(e) => setEditForm({...editForm, date_of_loss: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_date_of_assignment">Date of Assignment</Label>
                  <Input
                    id="edit_date_of_assignment"
                    type="date"
                    value={editForm.date_of_assignment}
                    onChange={(e) => setEditForm({...editForm, date_of_assignment: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_policy_number">Policy Number</Label>
                  <Input
                    id="edit_policy_number"
                    value={editForm.policy_number}
                    onChange={(e) => setEditForm({...editForm, policy_number: e.target.value})}
                    placeholder="Policy #"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_claim_number">Claim Number</Label>
                  <Input
                    id="edit_claim_number"
                    value={editForm.claim_number}
                    onChange={(e) => setEditForm({...editForm, claim_number: e.target.value})}
                    placeholder="Claim #"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_our_estimate_value">Our Estimate Value ($) <span className="text-red-500">*</span></Label>
                  <Input
                    id="edit_our_estimate_value"
                    type="number"
                    step="0.01"
                    value={editForm.our_estimate_value}
                    onChange={(e) => handleEditEstimateValueChange(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_expected_loss_settlement">Expected Loss Settlement ($)</Label>
                  <Input
                    id="edit_expected_loss_settlement"
                    type="number"
                    step="0.01"
                    value={editForm.expected_loss_settlement}
                    readOnly
                    className="bg-slate-100"
                    placeholder="Auto-calculated (75% of estimate)"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_fee_percentage">Fee Percentage (%) <span className="text-red-500">*</span></Label>
                  <Input
                    id="edit_fee_percentage"
                    type="number"
                    step="0.01"
                    value={editForm.fee_percentage}
                    onChange={(e) => handleEditFeePercentageChange(e.target.value)}
                    required
                    placeholder="Enter percentage"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_estimate_fee">Estimate Fee ($)</Label>
                  <Input
                    id="edit_estimate_fee"
                    type="number"
                    step="0.01"
                    value={editForm.estimate_fee}
                    readOnly
                    className="bg-slate-100"
                    placeholder="Auto-calculated"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_actual_loss_settlement">Actual Loss Settlement ($)</Label>
                  <Input
                    id="edit_actual_loss_settlement"
                    type="number"
                    step="0.01"
                    value={editForm.actual_loss_settlement}
                    onChange={(e) => handleEditActualSettlementChange(e.target.value)}
                    placeholder="Enter actual amount"
                  />
                  <p className="text-xs text-foreground-muted">
                    If entered, fee will be calculated based on this amount
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_status">Status <span className="text-red-500">*</span></Label>
                  <Select 
                    value={editForm.status} 
                    onValueChange={(value) => setEditForm({...editForm, status: value})}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="Open">Open</SelectItem>
                      <SelectItem value="Pending Docs">Pending Docs</SelectItem>
                      <SelectItem value="Negotiation">Negotiation</SelectItem>
                      <SelectItem value="Settled">Settled</SelectItem>
                      <SelectItem value="Paid in Full">Paid in Full</SelectItem>
                      <SelectItem value="Closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_firm">Firm</Label>
                  <Select 
                    value={editForm.firm} 
                    onValueChange={(value) => setEditForm({...editForm, firm: value})}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Select Firm" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="Apicella">Apicella</SelectItem>
                      <SelectItem value="MHA">MHA</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Catastrophe Event</Label>
                  <CatEventSelect
                    catEvents={catEvents.filter(e => e.status === 'Open' || e.cat_event_id === editForm.cat_event_id)}
                    value={editForm.cat_event_id}
                    onChange={(value) => setEditForm({...editForm, cat_event_id: value})}
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="edit_offer_received"
                  checked={editForm.offer_received}
                  onChange={(e) => setEditForm({...editForm, offer_received: e.target.checked})}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <Label htmlFor="edit_offer_received" className="cursor-pointer">Offer Received</Label>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_internal_comments">Internal Comments</Label>
                <Textarea
                  id="edit_internal_comments"
                  value={editForm.internal_comments}
                  onChange={(e) => setEditForm({...editForm, internal_comments: e.target.value})}
                  placeholder="Internal notes..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_notes">Notes</Label>
                <Textarea
                  id="edit_notes"
                  value={editForm.notes}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  placeholder="Additional notes..."
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-4">
                <Button type="button" variant="secondary" onClick={() => setIsEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary-hover" data-testid="submit-edit-claim">
                  Save Changes
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Fee Check Confirmation Dialog */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent className="bg-white">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Payment Record</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this payment record? This action cannot be undone and will recalculate the claim&apos;s fee balance.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setDeleteCheckId(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={confirmDeleteFeeCheck}
                className="bg-red-500 hover:bg-red-600"
                data-testid="confirm-delete-check"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};